import React, { useState, useEffect } from 'react';
import { luxColors, DNABox, DNAButton, Iffy } from '@alucio/lux-ui';
import { View, StyleSheet, Text } from 'react-native';
import { MenuItem, OverflowMenu } from '@ui-kitten/components';
import { ATTENDEE_STATUS } from '@alucio/aws-beacon-amplify/src/API';
import { useParticipants, Participant, useScreenShareToggle } from '../../../main';
import ParticipantTracks from '../../ParticipantTracks/ParticipantTracks';
import { useAttendeeShareScreen, AttendeeIsSharing, useMeetingControls, MeetingControlSelectedVideoDimensionEnum, } from '@alucio/video';
import { DeclineContent, DeclineContentSmall, AdmitDenyContent, AdmitDenyContentSmall, DiscconnectedContent, DiscconnectedContentSmall, } from './AcceptDeny';
export const ATTENDEE_GAP = 8;
const styles = StyleSheet.create({
    accept: {
        backgroundColor: luxColors.success.primary,
        borderRadius: 30,
        marginRight: 10,
    },
    acceptDenyContainer: {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        flexDirection: 'row',
        alignContent: 'center',
        alignSelf: 'center',
    },
    button: {
        height: 20,
        borderWidth: 0,
        width: 110,
    },
    deny: {
        backgroundColor: luxColors.error.primary,
        borderRadius: 30,
    },
    mainContainer: {
        width: 216,
        height: 120,
        marginLeft: 8,
        borderColor: luxColors.success.primary,
        backgroundColor: luxColors.contentPanelBackground.primary,
        borderWidth: 5,
    },
    name: {
        color: luxColors.info.primary,
        margin: 5,
        opacity: 0.8,
    },
    nameContainer: {
        backgroundColor: luxColors.opacityBackground.primary,
    },
    participant: {
        alignSelf: 'center',
        width: 216,
        height: 120,
        marginLeft: ATTENDEE_GAP,
    },
    rightMenu: {
        alignSelf: 'flex-end',
        zIndex: 100,
    },
    rightMenuButtonIcon: {
        color: 'white',
    },
});
function connected(props) {
    props.onAttendeeConnected && props.onAttendeeConnected(props.attendeeId, props.identity);
}
function RightMenu(props) {
    const [menuVisible, setMenuVisible] = useState(false);
    const isPresenting = AttendeeIsSharing(props.participant);
    function toggleMenu() {
        setMenuVisible(visible => !visible);
    }
    function handleRemovePressed() {
        setMenuVisible(false);
        props.onRemoveFromCall && props.onRemoveFromCall();
    }
    function askToShareScreen() {
        setMenuVisible(false);
        props.askToShareScreen && props.askToShareScreen(!isPresenting);
    }
    const anchor = (React.createElement(View, { style: styles.rightMenu },
        React.createElement(DNAButton, { size: "medium", status: "dark", context: "minimum", onPress: toggleMenu, iconLeft: "dots-vertical", style: { marginRight: 4, marginTop: 4 }, 
            // @ts-ignore
            iconStyle: { color: 'white' } })));
    return (React.createElement(OverflowMenu, { anchor: () => anchor, visible: menuVisible, onBackdropPress: () => setMenuVisible(false) },
        React.createElement(MenuItem, { title: isPresenting ? 'Stop presenting' : 'Ask to share screen', onPress: askToShareScreen }),
        React.createElement(MenuItem, { title: 'Remove', onPress: handleRemovePressed })));
}
function Attendee(props) {
    const participants = useParticipants();
    const { sendMessage } = useAttendeeShareScreen();
    const [, , , stopSharing] = useScreenShareToggle();
    const { videoSize, currentVideoDimension, onStopPresenting } = useMeetingControls();
    // An attendee can be represented by up to two participants one for the video/computer audio feed and one for phone
    // The identity string has the format of {attendeeId}.{video|phone}.{name}
    const filterParticipant = (identity, attendeeId, type) => {
        const idParts = identity.split('.');
        return idParts[0] === attendeeId && idParts[1] === type;
    };
    const videoParticipant = participants.find(e => filterParticipant(e.identity, props.attendeeId, 'video'));
    const phoneParticipant = participants.find(e => filterParticipant(e.identity, props.attendeeId, 'phone'));
    const participant = videoParticipant || phoneParticipant;
    useEffect(() => {
        videoParticipant && connected(props);
    }, []);
    function buildOptionsByStatus(status) {
        if (status === ATTENDEE_STATUS.PENDING) {
            return (React.createElement(React.Fragment, null, props.isRoomFull
                ? React.createElement(DNABox, null,
                    React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.STANDARD },
                        React.createElement(DeclineContent, { onDeny: onDeny })),
                    React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.SMALL },
                        React.createElement(DeclineContentSmall, { onDeny: onDeny })))
                : React.createElement(DNABox, null,
                    React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.STANDARD },
                        React.createElement(AdmitDenyContent, { onAccept: onAccept, onDeny: onDeny })),
                    React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.SMALL },
                        React.createElement(AdmitDenyContentSmall, { onAccept: onAccept, onDeny: onDeny })))));
        }
        else if (status === ATTENDEE_STATUS.DISCONNECTED) {
            return (React.createElement(React.Fragment, null,
                React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.STANDARD },
                    React.createElement(DiscconnectedContent, { name: props.name, onDeny: onDeny })),
                React.createElement(Iffy, { is: currentVideoDimension === MeetingControlSelectedVideoDimensionEnum.SMALL },
                    React.createElement(DiscconnectedContentSmall, { name: props.name, onDeny: onDeny }))));
        }
        else {
            return (React.createElement(React.Fragment, null,
                React.createElement(Text, { style: styles.name }, "Connecting...")));
        }
    }
    const acceptDenyContainer = buildOptionsByStatus(props.status);
    const hostControls = props.showHostControls
        ? (React.createElement(RightMenu, { participant: participant, onRemoveFromCall: onRemoveFromCall, askToShareScreen: askToShareScreen }))
        : null;
    function onAccept() {
        if (props.showHostControls) {
            props.onAcceptCall && props.onAcceptCall(props.attendeeId, props.identity);
        }
    }
    function onDeny() {
        if (props.showHostControls) {
            props.onDenyCall && props.onDenyCall(props.attendeeId, props.identity);
        }
    }
    function onRemoveFromCall() {
        if (props.showHostControls) {
            props.onRemoveFromCall && props.onRemoveFromCall(props.attendeeId, props.identity);
        }
    }
    function askToShareScreen(share) {
        if (props.showHostControls) {
            sendMessage(participant, share);
            stopSharing();
            onStopPresenting && onStopPresenting();
        }
    }
    return (participant
        ? React.createElement(View, { key: props.identity, style: [
                styles.participant, props.style,
                // If the recorder is present should be there but not visible
                props.identity.startsWith('recorder') && { maxWidth: 0 },
                { width: videoSize.container.width, height: videoSize.container.height },
            ] },
            React.createElement(Iffy, { is: !props.identity.startsWith('recorder') },
                React.createElement(Participant, { key: participant.sid, participant: participant, disableAudio: props.localPhoneAudio, phoneParticipant: phoneParticipant, name: props.name, videoPriority: 'standard', menu: hostControls, isSelected: false, onClick: () => { } })),
            React.createElement(Iffy, { is: props.identity.startsWith('recorder') },
                React.createElement(ParticipantTracks, { participant: participant, phoneParticipant: phoneParticipant, disableAudio: true, videoPriority: "low" })))
        : React.createElement(View, { key: props.identity, style: [
                styles.mainContainer,
                { width: videoSize.container.width, height: videoSize.container.height },
            ] },
            props.status === ATTENDEE_STATUS.CONNECTED && React.createElement(RightMenu, { onRemoveFromCall: onRemoveFromCall }),
            React.createElement(View, { style: styles.acceptDenyContainer }, acceptDenyContainer),
            React.createElement(View, { style: [styles.nameContainer] },
                React.createElement(Text, { style: styles.name, numberOfLines: 1 }, `${props.name}`))));
}
export default React.memo(Attendee);
//# sourceMappingURL=Attendee.js.map